import * as React from 'react';
import {useSelector} from 'react-redux';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Assignables} from 'nutshell-core/assignables';
import {
    safelyGetFilterByKey,
    safelyGetRelationshipFilterIdsFromFilterValue,
} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {LAST_UPDATED_VALUE_OPTIONS} from 'shells/control-bar/constants';
import {ExpandedFilterBar} from 'shells/control-bar/expanded-filter-bar';
import {
    getArchivedValue,
    getEditionStatusesForQuery,
    getLastUpdatedValue,
} from 'shells/control-bar/helpers';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {ViewBoardIcon as BoardIcon} from 'shells/icon/svg/view-board-icon';
import {ViewListIcon as ListIcon} from 'shells/icon/svg/view-list-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {PaginatedView} from 'shells/list-view/paginated-view';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {BroadcastStubFragment as Broadcast, SessionUser} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';
import type {NutshellState} from '../../../store';
import {writeLocalStorage} from '../../../util/localStorage';

import {GetSessionUser} from '../../core/get-session-user';
import {PAGE_CONTAINER_CONTENT_ID, PageContainer, PageTitleBar} from '../../layout';

import {checkIsFiltering} from '../email-marketing-helpers';
import {BroadcastListQuery} from '../graphql-wrappers/broadcast/broadcast-list-query';
import {BroadcastMutations} from '../graphql-wrappers/broadcast/broadcast-mutations';
import {getBroadcastListQueryVariables} from '../graphql-wrappers/helpers';
import {MarketingStatsBanner} from '../marketing-stats-banner/marketing-stats-banner';

import {BroadcastStats} from './broadcast-stats';
import {BroadcastsList} from './broadcasts-list';
import {NewBroadcastModal} from './new-broadcast-modal';

type OwnProps = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    broadcasts: Broadcast[];
    onDuplicateBroadcast: (id: string, name?: string | null | undefined) => Promise<any>;
    onDeleteBroadcast: (arg1: string) => Promise<any>;
    onRestoreBroadcast: (arg1: string) => Promise<any>;
    isLoading: boolean;
    isFirstLoad: boolean;
    error: string | null | undefined;
    searchValue: string;
    userId: string;
    onParamChange: (arg1: any, arg2: boolean) => any;
    activeFilters: FilterObject[];
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;
    hasNextPage: boolean;
    onFetchMoreRows: () => Promise<any>;
    user: SessionUser;
};

const BROADCAST_VIEW_SEARCH = 'BROADCAST_VIEW_SEARCH';

function BroadcastsListPageComponent(props: Props) {
    const [isAddingNewBroadcast, setIsAddingNewBroadcast] = React.useState<boolean>(false);
    const [duplicatingBroadcast, setDuplicatingBroadcast] = React.useState<
        Broadcast | null | undefined
    >(null);
    const [isDuplicatingBroadcast, setIsDuplicatingBroadcast] = React.useState<boolean>(false);
    const assignables = useSelector((state: NutshellState) => Assignables.getAll(state));

    const selectedTab = props.urlParams.view || 'board';

    // Support ?create=true from command palette AI action
    React.useEffect(() => {
        if (props.urlParams.create === 'true') {
            setIsAddingNewBroadcast(true);
            // Clear the param so refresh doesn't re-trigger
            props.onParamChange({create: undefined}, true);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        writeLocalStorage(BROADCAST_VIEW_SEARCH, props.location.search);
    }, [props.location.search]);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    const isFiltering = checkIsFiltering(props.activeFilters, props.searchValue);

    const ownerFilterProps = {
        ownerFilter: safelyGetFilterByKey(props.activeFilters, 'owner'),
        collection: assignables,
    } as const;
    const justMineFilterProps = {
        isFilteringOnCreatorUserId: isFilteringOnCreatorUserId,
        userId: props.userId,
    } as const;

    const tabOptions = [
        {
            label: 'Board',
            id: 'board',
            icon: BoardIcon,
            href: props.getUpdatedUrl({
                view: 'board',
            }),
            component: (
                <PaginatedView
                    scrollContainerId={PAGE_CONTAINER_CONTENT_ID}
                    hasNextPage={props.hasNextPage}
                    isLoading={props.isLoading && !props.isFirstLoad && !isFiltering}
                    onFetchMoreRows={props.onFetchMoreRows}
                    renderBody={
                        <BroadcastsList
                            broadcasts={props.broadcasts}
                            onDeleteBroadcast={props.onDeleteBroadcast}
                            onRestoreBroadcast={props.onRestoreBroadcast}
                            isLoading={props.isLoading}
                            isFiltering={isFiltering}
                            error={props.error}
                            setDuplicatingBroadcast={setDuplicatingBroadcast}
                            setIsAddingNewBroadcast={setIsAddingNewBroadcast}
                            hasFullAccessToCampaigns={props.user.permissions.canAccessFullCampaigns}
                        />
                    }
                />
            ),
        },
        {
            label: 'Table',
            id: 'table',
            icon: ListIcon,
            href: props.getUpdatedUrl({
                view: 'table',
            }),
            component: (
                <PaginatedView
                    hasNextPage={props.hasNextPage}
                    isLoading={props.isLoading}
                    onFetchMoreRows={props.onFetchMoreRows}
                    renderBody={
                        <BroadcastStats
                            // @ts-expect-error
                            editions={props.broadcasts.map((broadcast) => broadcast.edition)}
                            isLoading={props.isLoading}
                            isFiltering={isFiltering}
                            error={props.error}
                            sortString={props.urlParams.sort}
                            onChangeSort={(newSortString) => {
                                props.onParamChange({sort: newSortString}, true);
                            }}
                            setIsAddingNewEdition={setIsAddingNewBroadcast}
                            hasFullAccessToCampaigns={props.user.permissions.canAccessFullCampaigns}
                        />
                    }
                />
            ),
        },
    ];

    return (
        <>
            <PageContainer
                title='Broadcasts'
                pageTitleBar={
                    <PageTitleBar
                        title='Broadcasts'
                        subheaderPrimaryText='Reach out to thousands at a time with one well-designed message'
                        buttons={
                            <Button
                                variant='primary'
                                size='big'
                                onClick={() => {
                                    setIsAddingNewBroadcast(true);
                                }}
                            >
                                Create new broadcast
                            </Button>
                        }
                    />
                }
                controlBar={
                    <ListPageControlBar
                        archiveTab={{
                            isActive: isShowingArchived,
                            onChange: () => {
                                props.onUpdateFilters({
                                    archived: isShowingArchived ? null : true,
                                });
                            },
                        }}
                        tabs={{
                            selectedTab: selectedTab,
                            options: tabOptions,
                        }}
                        leftSection={
                            <ExpandedFilterBar
                                ownerFilterProps={
                                    props.user.permissions.canAccessFullCampaigns
                                        ? ownerFilterProps
                                        : undefined
                                }
                                statusFilterProps={{
                                    statusFilter: safelyGetFilterByKey(
                                        props.activeFilters,
                                        'status'
                                    ),
                                    statusOptions: ['Draft', 'Scheduled', 'Halted', 'Sent'],
                                    statusDisabled: isShowingArchived,
                                }}
                                lastUpdatedFilterProps={{
                                    lastUpdatedFilter: safelyGetFilterByKey(
                                        props.activeFilters,
                                        'lastUpdated'
                                    ),
                                    // @ts-expect-error
                                    lastUpdatedOptions: LAST_UPDATED_VALUE_OPTIONS,
                                }}
                                justMineFilterProps={justMineFilterProps}
                                archivedFilterProps={{
                                    isFilteringOnArchived: isShowingArchived,
                                }}
                                searchFilterProps={{
                                    searchValue: props.searchValue,
                                    onUpdateSearch: (newSearchValue) => {
                                        props.onParamChange({q: newSearchValue}, true);
                                    },
                                }}
                                isFiltering={isFiltering}
                                noun='broadcast'
                                count={props.broadcasts.length}
                                onUpdateFilters={props.onUpdateFilters}
                                onRemoveAllFilters={props.onRemoveAllFilters}
                                activeFilters={props.activeFilters}
                            />
                        }
                    />
                }
            >
                <MarketingStatsBanner isImportUnsubscribesBannerNeeded={true} />
                {/* @ts-expect-error */}
                <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
            </PageContainer>
            <NewBroadcastModal
                isOpen={isAddingNewBroadcast}
                onClose={() => {
                    setIsAddingNewBroadcast(false);
                }}
            />
            {duplicatingBroadcast ? (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Create'
                    initialValue={`Copy of ${duplicatingBroadcast.name}`}
                    title='Name your new broadcast'
                    subtitle='Add a descriptive name so you can find it later'
                    placeholder={`Copy of ${duplicatingBroadcast.name}`}
                    isSaving={isDuplicatingBroadcast}
                    onClose={() => {
                        setDuplicatingBroadcast(null);
                    }}
                    onSave={(name) => {
                        setIsDuplicatingBroadcast(true);
                        const nameValue = name && name.length ? name : null;
                        props.onDuplicateBroadcast(duplicatingBroadcast.edition.id, nameValue);
                    }}
                />
            ) : undefined}
        </>
    );
}

export function BroadcastsListPage(props: OwnProps) {
    // We've hardcoded the server's sort for now - table sorting is currently
    // handled on the client and stored in the URL
    const sortValue = 'MODIFIED_TIME_DESC';
    const searchValue = props.urlParams.q || '';
    const isArchived = getArchivedValue(props.activeFilters);

    const ownerFilter = safelyGetFilterByKey(props.activeFilters, 'owner');
    const assigneeIds = ownerFilter
        ? safelyGetRelationshipFilterIdsFromFilterValue(ownerFilter)
        : undefined;

    const queryVariables = getBroadcastListQueryVariables(isArchived, sortValue, searchValue);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const lastUpdatedTime = getLastUpdatedValue(props.activeFilters);
    const statuses = getEditionStatusesForQuery(props.activeFilters);

    return (
        <GetSessionUser>
            {/* @ts-expect-error */}
            {({user}) => {
                return user ? (
                    <BroadcastListQuery
                        assigneeIds={assigneeIds}
                        isArchived={isArchived}
                        sortOrder={sortValue}
                        searchQuery={searchValue}
                        creatorUserId={isFilteringOnCreatorUserId ? user.id : undefined}
                        lastUpdatedTime={lastUpdatedTime}
                        statuses={statuses && statuses.length ? statuses : undefined}
                    >
                        {({
                            broadcasts,
                            isLoading,
                            isFirstLoad,
                            error,
                            fetchMoreRows,
                            hasNextPage,
                        }) => (
                            <BroadcastMutations>
                                {({onDuplicate, onDelete, onRestore}) => (
                                    <BroadcastsListPageComponent
                                        {...props}
                                        broadcasts={broadcasts}
                                        isLoading={isLoading}
                                        isFirstLoad={isFirstLoad}
                                        error={error}
                                        // @ts-expect-error
                                        onDuplicateBroadcast={(broadcastId, name) =>
                                            onDuplicate(broadcastId, name, queryVariables).then(
                                                (newBroadcast) => {
                                                    if (newBroadcast)
                                                        appHistory.push(
                                                            newBroadcast.edition.htmlUrlPath
                                                        );
                                                }
                                            )
                                        }
                                        // @ts-expect-error
                                        onDeleteBroadcast={(broadcastId) => {
                                            return onDelete(broadcastId, queryVariables);
                                        }}
                                        // @ts-expect-error
                                        onRestoreBroadcast={(broadcastId) => {
                                            return onRestore(broadcastId, queryVariables);
                                        }}
                                        searchValue={searchValue}
                                        userId={user.id}
                                        onFetchMoreRows={fetchMoreRows}
                                        hasNextPage={hasNextPage}
                                        user={user}
                                    />
                                )}
                            </BroadcastMutations>
                        )}
                    </BroadcastListQuery>
                ) : null;
            }}
        </GetSessionUser>
    );
}
